import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, MapPin, Truck, Building2, Palette, PaintBucket, Sun, Check, ChevronDown, Play, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import TrustRibbon from "@/components/TrustRibbon";
import SEO from "@/components/SEO";
import { ProjectCard } from "@/components/ProjectCard";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };

const HERO_IMG = "https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/swlwmcsv_IMG_0586.webp";
const AZ_IMG = "https://images.pexels.com/photos/4061938/pexels-photo-4061938.jpeg?auto=compress&cs=tinysrgb&w=1600";
const DAVE_IMG = "https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/dmpaw94x_Webpage_MainImage.webp";
const TRUCK_BILLBOARD_IMG = "https://images.unsplash.com/photo-1573495407624-1e9534cf61e2?auto=format&fit=crop&w=1600&q=80";
const FINAL_CTA_IMG = "https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/sfmqpd3j_IMG_5167-scaled.webp";

function CTA({ children = "Get My FREE Mockup", testid, className = "", size = "md", location = "unknown" }) {
  const sizes = { md: "!py-3.5 !px-6 text-sm", lg: "!py-4 !px-7 text-base" };
  return (
    <Link to="/mockup" data-testid={testid} data-track="cta_click" data-track-location={location} className={`btn-red ${sizes[size]} ${className}`}>
      {children} <ArrowRight size={16} />
    </Link>
  );
}

function StatCounter({ end, suffix = "", label }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const start = performance.now();
          const step = (t) => {
            const p = Math.min(1, (t - start) / 1200);
            setVal(Math.round(end * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [end]);
  return (
    <div ref={ref} className="flex flex-col">
      <span className="font-display font-extrabold text-[var(--ink)]" style={{ fontSize: "clamp(48px, 8vw, 96px)", lineHeight: 1, letterSpacing: "-0.04em" }}>{val}{suffix}</span>
      <span className="eyebrow mt-2">{label}</span>
    </div>
  );
}

const services = [
  { icon: Truck, title: "Commercial Vehicle Wraps", desc: "Rolling billboards that work 24/7. Custom-designed to make your business impossible to ignore.", span: "md:col-span-2", featured: true, slug: "commercial-vehicle-wraps" },
  { icon: Building2, title: "Fleet Wraps", desc: "Consistent, professional branding across every truck, van, and trailer.", span: "", slug: "fleet-wraps" },
  { icon: Sun, title: "Automotive Window Tint", desc: "Cabin comfort, UV protection, and a factory-clean finish. Legal Arizona shades.", span: "", slug: "automotive-window-tint" },
  { icon: Sun, title: "Residential & Commercial Tint", desc: "Cut glare, lower energy bills, protect interiors — for homes, offices, and storefronts.", span: "md:col-span-2", slug: "residential-window-tint" },
  { icon: PaintBucket, title: "Signs, Banners & Custom Graphics", desc: "Storefront signage, event banners, wall graphics — a complete visual identity system.", span: "md:col-span-2", slug: "signs-and-banners" },
];

const faqs = [
  { q: "How long does a vehicle wrap last?", a: "A properly installed commercial vehicle wrap made from cast vinyl typically lasts 5 to 7 years in Northern Arizona conditions. Longevity depends on film quality, install technique, sun exposure, and care. We only use premium cast vinyl rated for Arizona's high-altitude sun." },
  { q: "Will the wrap damage my paint?", a: "No — the opposite. A properly installed wrap actually protects your factory paint from UV, minor scratches, and road debris. When removed correctly, the paint underneath is often in better shape than the rest of the vehicle." },
  { q: "How much downtime is required for installation?", a: "Most commercial wraps take 1 to 2 days from drop-off to pickup. Fleet installs are scheduled in phases so you never lose your whole fleet at once. Simpler jobs — partial wraps, lettering, graphics — often turn around same-day." },
  { q: "Can you help with design if I don't have a logo yet?", a: "Absolutely. Custom design is included in every project. If you don't have a logo — or your current one needs a refresh — we handle it. Dave reviews every design personally before it gets printed." },
  { q: "Do you warranty your work?", a: "Yes — every project is backed by our 1-year craftsmanship warranty. On top of that, you get the film manufacturer's own material warranty from 3M or Avery. If anything isn't right on our end, we make it right — no fine print." },
  { q: "What areas do you serve?", a: "Prescott Valley (our shop), Prescott, Chino Valley, Dewey-Humboldt, and all of Northern Arizona. If you're within driving distance, we can help. Fleet clients occasionally travel further — just ask." },
];

export default function Home() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <div data-testid="page-home">
      <SEO
        title="928 Wraps | Vehicle Wraps & Branding in Prescott Valley, AZ"
        description="Helping Northern Arizona businesses get seen. Premium commercial vehicle wraps, fleet branding, window tint, and signage in Prescott Valley. Free custom mockup — no obligation."
        path="/"
        ogImage={HERO_IMG}
      />

      {/* ═══════════════════ 01 · HERO ═══════════════════ */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        {/* Mobile-only background video */}
        <div className="md:hidden absolute inset-0 z-0" aria-hidden="true">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/hero-poster.jpg"
            className="w-full h-full object-cover"
          >
            <source src="/hero.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="wrap grid md:grid-cols-12 gap-10 md:gap-12 items-center relative z-10">
          <motion.div variants={stagger} initial="hidden" animate="show" className="md:col-span-7 relative z-10">
            <motion.div variants={fadeUp} className="chip">
              <span className="w-2 h-2 rounded-full bg-[var(--red)]" /> Prescott Valley · Northern Arizona
            </motion.div>
            <motion.h1 variants={fadeUp} className="h-display mt-6 text-white md:text-[var(--ink)]" style={{ fontSize: "clamp(52px, 5.2vw, 68px)" }}>
              Every vehicle. <span className="text-[var(--red)]">Every window.</span> Every wall.
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 text-lg text-neutral-200 md:text-neutral-600 max-w-xl leading-relaxed">
              Commercial wraps &amp; fleet branding, ceramic window tint for cars, homes &amp; offices, and custom signs &amp; banners — designed, printed, and installed by one owner-operated team in Prescott Valley.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
              <CTA testid="hero-cta-mockup" size="lg" location="hero">Get My FREE Quote</CTA>
              <Link to="/portfolio" data-testid="hero-cta-portfolio" className="btn-ghost bg-white/10 md:bg-transparent !text-white md:!text-[var(--ink)] border-white/30 md:border-[var(--line)]">
                View Our Work <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-8 flex items-center gap-4 text-sm">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-[var(--red)] text-[var(--red)]" />)}
              </div>
              <span className="font-semibold text-neutral-100 md:text-neutral-800">Owner operated since 2012</span>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="hidden md:block md:col-span-5 relative">
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[24px] bg-neutral-900">
              <video
                autoPlay
                muted
                loop
                playsInline
                poster="/hero-poster.jpg"
                className="absolute inset-0 w-full h-full object-cover"
                aria-label="928 Wraps shop and install footage"
              >
                <source src="/hero.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute left-6 bottom-6 right-6">
                <div className="eyebrow !text-white">Inside the shop · Prescott Valley</div>
                <div className="font-display text-2xl text-white mt-1 font-bold">Design · Print · Install — one team.</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mobile-only: tap to watch full video */}
      <button
        type="button"
        onClick={() => setVideoOpen(true)}
        data-testid="mobile-watch-video"
        data-track="video_open"
        data-track-location="mobile_hero"
        className="md:hidden w-full flex items-center gap-4 px-6 py-4 border-y border-neutral-200 bg-white"
      >
        <span className="w-11 h-11 rounded-full bg-[var(--red)] text-white grid place-items-center flex-shrink-0">
          <Play size={18} className="ml-0.5 fill-white" />
        </span>
        <span className="text-left">
          <span className="block font-semibold text-neutral-900 text-sm">Watch our shop in action</span>
          <span className="block text-neutral-500 text-xs mt-0.5">Tap to play the full video</span>
        </span>
        <ArrowRight size={16} className="ml-auto text-neutral-400" />
      </button>

      {/* Fullscreen video player (mobile) */}
      {videoOpen && (
        <div
          className="md:hidden fixed inset-0 z-[100] bg-black flex items-center justify-center"
          data-testid="mobile-video-modal"
          onClick={() => setVideoOpen(false)}
        >
          <button
            onClick={() => setVideoOpen(false)}
            data-testid="mobile-video-close"
            aria-label="Close video"
            className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-white/15 backdrop-blur text-white grid place-items-center"
            style={{ marginTop: "env(safe-area-inset-top, 0)" }}
          >
            <X size={20} />
          </button>
          <video
            src="/hero.mp4"
            controls
            autoPlay
            playsInline
            className="w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* ═══════════════════ 02 · TRUST RIBBON ═══════════════════ */}
      <TrustRibbon />


      {/* Trust stats — verified from Google Business Profile */}
      <section className="py-16 md:py-20">
        <div className="wrap grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="flex flex-col">
            <span className="font-display font-extrabold text-[var(--ink)]" style={{ fontSize: "clamp(48px, 8vw, 96px)", lineHeight: 1, letterSpacing: "-0.04em" }}>5.0<span className="text-[var(--red)]">★</span></span>
            <span className="eyebrow mt-2">Google rating</span>
          </div>
          <StatCounter end={4382} suffix="" label="Projects completed" />
          <StatCounter end={13} suffix="+" label="Years serving AZ" />
          <StatCounter end={1} suffix=" yr" label="Craftsmanship warranty" />
        </div>
      </section>

      {/* ═══════════════════ 03 · WHY THIS MATTERS (THE PIVOT) ═══════════════════ */}
      <section className="py-24 md:py-32 bg-[var(--bg-2)]">
        <div className="wrap grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 md:sticky md:top-32">
            <div className="eyebrow eyebrow-red">The pivot</div>
            <h2 className="h-display mt-4" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
              Your truck is already advertising your business.
            </h2>
            <p className="mt-6 text-xl text-neutral-700 leading-relaxed">
              The only question is — <em className="not-italic underline decoration-[var(--red)] decoration-2 underline-offset-4">is it working for you?</em>
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-8">
            <div>
              <div className="eyebrow">01</div>
              <p className="mt-3 text-lg text-neutral-700 leading-relaxed">
                The average work truck generates over <strong className="text-[var(--ink)]">30,000 visual impressions per day</strong>. Every commute, every job site, every stoplight — impressions that either grow your business or quietly pass by.
              </p>
            </div>
            <div className="border-t border-neutral-200 pt-8">
              <div className="eyebrow">02</div>
              <p className="mt-3 text-lg text-neutral-700 leading-relaxed">
                Your truck arrives in the driveway before you do. It&apos;s the first sentence in every customer conversation — before you shake a hand, before you quote a job.
              </p>
            </div>
            <div className="border-t border-neutral-200 pt-8">
              <div className="eyebrow">03</div>
              <p className="mt-3 text-lg text-neutral-700 leading-relaxed">
                Memorable trucks get talked about. A neighbor sees the wrap, asks the homeowner, calls the number. That&apos;s referral velocity you can&apos;t buy through ads.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 04 · RESULTS OVER PRICE ═══════════════════ */}
      <section className="py-24 md:py-32">
        <div className="wrap">
          <div className="max-w-3xl">
            <div className="eyebrow eyebrow-red">The shift</div>
            <h2 className="h-display mt-4" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
              It&apos;s not about the cost. <span className="italic font-normal text-neutral-500">It&apos;s about the opportunity.</span>
            </h2>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-8 md:gap-16">
            <div>
              <div className="eyebrow text-neutral-500">Cost-based thinking</div>
              <div className="mt-6 space-y-4 text-neutral-500">
                {["How cheap can we make this?", "How fast can we ship it?", "Just get it done.", "Compare three quotes."].map((t) => (
                  <div key={t} className="flex items-center gap-3 border-b border-neutral-100 pb-4">
                    <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full" />
                    <span className="text-lg line-through decoration-neutral-300">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="eyebrow eyebrow-red">Opportunity-based thinking</div>
              <div className="mt-6 space-y-4">
                {["How many new customers could this bring?", "How memorable does my brand feel?", "What&apos;s the ROI over five years?", "Am I proud to drive it every day?"].map((t) => (
                  <div key={t} className="flex items-center gap-3 border-b border-neutral-200 pb-4">
                    <span className="w-1.5 h-1.5 bg-[var(--red)] rounded-full" />
                    <span className="text-lg text-neutral-900 font-medium" dangerouslySetInnerHTML={{ __html: t }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-16 max-w-3xl text-xl md:text-2xl text-neutral-800 leading-snug font-medium">
            A professionally branded vehicle is the only marketing expense that pays you back <em className="not-italic text-[var(--red)]">every time you drive it.</em>
          </p>
        </div>
      </section>

      {/* ═══════════════════ 05 · MEET DAVE ═══════════════════ */}
      <section className="py-24 md:py-32 bg-[var(--bg-2)]">
        <div className="wrap grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-white rounded-3xl -z-10" />
              <img
                src={DAVE_IMG}
                alt="Dave Mitchell, owner of 928 Wraps, with the wrapped shop truck in Northern Arizona"
                className="rounded-2xl w-full aspect-[3/4] object-cover object-center"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-6 bg-[var(--red)] text-white px-5 py-4 rounded-2xl shadow-lg">
                <div className="text-xs tracking-[0.2em] uppercase font-bold">Owner</div>
                <div className="font-display text-2xl">Dave Mitchell</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="eyebrow">The human element</div>
            <h2 className="h-display mt-3" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
              You&apos;re hiring <span className="italic font-normal">Dave</span> — not a corporation.
            </h2>
            <p className="mt-6 text-lg text-neutral-700 leading-relaxed">
              People don&apos;t recommend 928 Wraps because of the wraps. They recommend it because of Dave — he shows up, he listens, and he treats your business like it&apos;s his own.
            </p>
            <p className="mt-4 text-neutral-500 leading-relaxed">
              No pushy sales scripts. No mystery pricing. Just an experienced local craftsman who genuinely wants your business to look sharp and grow.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="chip">Installing since 2012</span>
              <span className="chip">Prescott Valley local</span>
              <span className="chip">Owner on every project</span>
            </div>
            <Link to="/about" data-testid="dave-story-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ink)] hover:text-[var(--red)]">
              Read Dave&apos;s story <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 06 · THE 928 PROMISE ═══════════════════ */}
      <section className="py-24 md:py-32 bg-[var(--ink)] text-white relative overflow-hidden">
        <div className="wrap relative z-10">
          <div className="max-w-3xl">
            <div className="eyebrow eyebrow-red">The 928 promise</div>
            <h2 className="h-display mt-4" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
              What every customer gets. <span className="italic font-normal text-neutral-500">Every time.</span>
            </h2>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-12">
            {[
              { n: "01", t: "Premium materials", d: "3M and Avery cast vinyl only. Rated for Arizona sun, high altitude, and years on the road — so your investment still looks new down the line." },
              { n: "02", t: "Custom design", d: "No templates. No copy-paste. Every project is designed around your brand and your vehicle — because that&apos;s the whole point of doing this right." },
              { n: "03", t: "Standing behind our work", d: "If it&apos;s not right, we fix it. Not because a policy says so — because that&apos;s how we sleep at night. Every project comes with our workmanship guarantee." },
            ].map((p) => (
              <div key={p.n}>
                <div className="w-11 h-[2px] bg-[var(--red)]" />
                <div className="font-mono text-xs text-neutral-500 mt-4 tracking-[0.2em]">{p.n}</div>
                <h3 className="font-display text-2xl md:text-3xl mt-3">{p.t}</h3>
                <p className="mt-4 text-neutral-400 leading-relaxed" dangerouslySetInnerHTML={{ __html: p.d }} />
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -right-32 -bottom-32 w-[600px] h-[600px] rounded-full bg-[var(--red)] opacity-[0.05] blur-3xl" />
      </section>

      {/* ═══════════════════ 07 · FEATURED TRANSFORMATIONS ═══════════════════ */}
      <section className="py-24 md:py-32">
        <div className="wrap">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow">Featured work</div>
              <h2 className="h-display mt-3" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
                See what visibility <br className="hidden md:block" /><span className="italic font-normal">looks like.</span>
              </h2>
            </div>
            <Link to="/portfolio" data-testid="portfolio-link" className="btn-ghost self-start">Full portfolio <ArrowRight size={16} /></Link>
          </div>

          <div className="grid md:grid-cols-12 auto-rows-[260px] gap-4 md:gap-6">
            {[
              {
                title: "Doc's BBQ & Whiskey",
                tag: "Signature Cadillac Eldorado build · Prescott, AZ",
                images: [
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/gdsgdl1l_IMG_0845.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/bjnjxtyu_IMG_0848.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/njysukur_IMG_0854.webp",
                ],
                span: "md:col-span-8 md:row-span-2",
                position: "center",
              },
              {
                title: "The Fat Frog Sports Grill",
                tag: "Custom hot rod fleet · Prescott, AZ",
                images: [
                  "https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/hjqwh9mt_IMG_0790.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/yoqx75nc_IMG_0796.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/c29pxpid_IMG_0882.webp",
                ],
                span: "md:col-span-4 md:row-span-2",
                position: "center",
              },
              {
                title: "Findlay Toyota Prescott",
                tag: "America's 250th Anniversary Tundra",
                images: [
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/au6whybi_IMG_0443.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/pjqla80l_IMG_0444.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/qp5mts03_IMG_0445.webp",
                ],
                span: "md:col-span-4",
                position: "center",
              },
              {
                title: "Southwest Expedited",
                tag: "Hauling Freedom · Peterbilt semi",
                images: [
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/vqvke081_IMG_0871.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/vfikq0lt_IMG_0870.webp",
                ],
                span: "md:col-span-4",
                position: "center",
              },
              {
                title: "928 Wraps · Shop Truck",
                tag: "Full commercial wrap · Watson Lake, AZ",
                images: ["https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/sfmqpd3j_IMG_5167-scaled.webp"],
                span: "md:col-span-4",
              },
              {
                title: "PRYME Coatings",
                tag: "Box truck wrap · Prescott, AZ",
                images: [
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/t1f97yvx_IMG_0465.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/h93st1h1_IMG_0466.webp",
                  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dave-wraps-studio/artifacts/6ghny1rz_IMG_0467.webp",
                ],
                span: "md:hidden",
                position: "center",
              },
              {
                title: "Prescott Epoxy Company",
                tag: "Box truck · Bold commercial wrap",
                images: ["https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/voknl8a0_Image-6-20-25-at-7.11%E2%80%AFPM.jpg"],
                span: "hidden md:block md:col-span-12",
              },
            ].map((p, i) => (
              <ProjectCard
                key={i}
                testId={`home-project-${i}`}
                title={p.title}
                tag={p.tag}
                images={p.images}
                span={p.span}
                position={p.position}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ 08 · SERVICES ═══════════════════ */}
      <section className="py-24 md:py-32 bg-[var(--bg-2)]">
        <div className="wrap">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow">What we do</div>
            <h2 className="h-display mt-3" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
              Full-service visual branding — with <span className="italic font-normal">commercial wraps</span> at the center.
            </h2>
          </div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }} className="grid md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <motion.div variants={fadeUp} key={s.title} className={`${s.span}`}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${i}`} data-track="service_card_click" data-track-location={`home_bento_${s.slug}`} className={`flat-card rounded-2xl p-8 h-full block ${s.featured ? "!bg-[var(--ink)] !text-white !border-[var(--ink)]" : ""}`}>
                  <div className="flex items-center justify-between">
                    <s.icon size={28} strokeWidth={1.5} className={s.featured ? "text-white" : "text-[var(--ink)]"} />
                    <span className={`eyebrow ${s.featured ? "!text-neutral-500" : ""}`}>0{i + 1}</span>
                  </div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl mt-8 leading-tight">{s.title}</h3>
                  <p className={`mt-4 leading-relaxed ${s.featured ? "text-neutral-300" : "text-neutral-600"}`}>{s.desc}</p>
                  <div className={`mt-6 inline-flex items-center gap-2 text-sm font-semibold ${s.featured ? "text-white" : "text-[var(--ink)]"}`}>
                    Explore <ArrowRight size={14} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-12">
            <Link to="/services" className="btn-ghost">Explore all services <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 09 · SIMPLE PROCESS ═══════════════════ */}
      <section className="py-24 md:py-32">
        <div className="wrap">
          <div className="max-w-3xl mb-16">
            <div className="eyebrow eyebrow-red">The 928 process</div>
            <h2 className="h-display mt-3" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
              We make this <span className="italic font-normal">easy.</span>
            </h2>
            <p className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-xl">
              Three steps between where you are today and driving away in something you&apos;re genuinely proud of.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
            <div className="hidden md:block absolute top-6 left-[16.66%] right-[16.66%] h-[2px] bg-neutral-200" />
            {[
              { n: "01", t: "Tell us about your project", d: "60-second form. Vehicle, service, and a few words about your business. That&apos;s it." },
              { n: "02", t: "Receive your FREE custom mockup", d: "We build a real design on your actual vehicle. See it before you commit to a single dollar." },
              { n: "03", t: "Bring your vision to life", d: "Approve the design, we print and install. Most projects wrap up in 2–3 weeks." },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="w-12 h-12 rounded-full bg-[var(--red)] text-white grid place-items-center font-mono text-sm font-bold relative z-10">{s.n}</div>
                <h3 className="font-display text-2xl md:text-3xl mt-6" dangerouslySetInnerHTML={{ __html: s.t }} />
                <p className="mt-3 text-neutral-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: s.d }} />
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-wrap items-center gap-4">
            <CTA testid="process-cta" size="lg" location="process" />
            <span className="text-sm text-neutral-500">Takes 90 seconds. No pressure. No commitment.</span>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 10 · REVIEWS (real Google reviews) ═══════════════════ */}
      <section className="py-24 md:py-32 bg-[var(--bg-2)]">
        <div className="wrap">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow eyebrow-red">What real customers say about working with Dave</div>
            <h2 className="h-display mt-3" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
              Every review below is from a customer who worked <span className="italic font-normal">directly with Dave.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { q: "Great communication, very nice people, and top-quality work.", n: "Jose Pere" },
              { q: "We just had a 2nd personal vehicle taken care of by 928 Wraps in the last 5 years.", n: "Pete Gordon" },
              { q: "His price is fair and his service is amazing.", n: "Brittany Alvarez" },
            ].map((t, i) => (
              <a key={i} href="https://www.google.com/maps/place/928+Wraps/data=!4m2!3m1!1s0x83bb0e299cb5635d:0x16765e0e0d34903" target="_blank" rel="noopener noreferrer" className="flat-card !bg-white rounded-2xl p-8 block" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, k) => <Star key={k} size={14} className="fill-[var(--red)] text-[var(--red)]" />)}
                </div>
                <p className="text-lg leading-relaxed text-neutral-800">&ldquo;{t.q}&rdquo;</p>
                <div className="mt-6 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-neutral-900">{t.n}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">Verified Google review</div>
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-neutral-400 flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-white border border-neutral-200 grid place-items-center font-display text-[10px] text-neutral-700">G</span>
                    via Google
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-10">
            <a href="https://www.google.com/maps/place/928+Wraps/data=!4m2!3m1!1s0x83bb0e299cb5635d:0x16765e0e0d34903" target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--ink)] hover:text-[var(--red)]">
              Read reviews on Google <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 11 · FAQ ═══════════════════ */}
      <section className="py-24 md:py-32">
        <div className="wrap max-w-3xl">
          <div className="eyebrow">Common questions</div>
          <h2 className="h-display mt-3" style={{ fontSize: "clamp(40px, 5vw, 72px)" }}>
            Everything you&apos;d probably <span className="italic font-normal">wonder</span> — answered.
          </h2>

          <Accordion type="single" collapsible className="mt-12 divide-y divide-neutral-200 border-y border-neutral-200">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-0" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl py-6 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-neutral-700 leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <p className="text-neutral-500">Still have questions?</p>
            <CTA testid="faq-cta" location="faq">Start with a free mockup</CTA>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 12 · FINAL CTA ═══════════════════ */}
      <section className="relative overflow-hidden">
        <div className="relative min-h-[80vh] md:min-h-[100vh] flex items-end">
          <img src={FINAL_CTA_IMG} alt="Wrapped vehicle at 928 Wraps in Prescott Valley, Arizona" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/50 to-black/20" />
          <div className="wrap relative z-10 pb-24 md:pb-32 pt-32">
            <div className="max-w-2xl text-white">
              <div className="eyebrow text-white/60">Your move</div>
              <h2 className="h-display mt-4" style={{ fontSize: "clamp(48px, 7vw, 96px)" }}>
                Ready to see what your vehicle <span className="italic font-normal">could look like?</span>
              </h2>
              <p className="mt-6 text-lg md:text-xl text-neutral-200 leading-relaxed max-w-xl">
                Let&apos;s create something you&apos;ll be proud to drive — and your customers won&apos;t forget.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <CTA testid="final-cta" size="lg" location="final_cta" />
                <span className="text-sm text-white/70">90-second form · No pressure · No obligation</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
